<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { adminLoginEventsApi } from '$lib/api/admin';
  import { hrUsersApi } from '$lib/api/hr';
  import Toolbar from '$lib/components/ui/Toolbar.svelte';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import StatusBadge, { type BadgeKind } from '$lib/components/ui/StatusBadge.svelte';
  import { relativeTime } from '$lib/utils/time';
  import type { LoginEvent, LoginEventStatus } from '$lib/types';

  let userFilter = $state('');
  let statusFilter = $state('');

  const query = createQuery(() => ({
    queryKey: ['admin-login-events', userFilter, statusFilter],
    queryFn: () => adminLoginEventsApi.list({ user_id: userFilter, status: statusFilter })
  }));
  const usersQuery = createQuery(() => ({
    queryKey: ['hr-users'],
    queryFn: () => hrUsersApi.list()
  }));

  const STATUS_LABEL: Record<LoginEventStatus, string> = {
    success: 'Success',
    bad_password: 'Bad password',
    unknown_email: 'Unknown email',
    device_mismatch: 'Device mismatch',
    mfa_required: 'MFA challenge issued',
    mfa_failed: 'MFA code failed',
    mfa_setup: 'MFA setup issued',
    deactivated: 'Deactivated account'
  };

  const STATUS_KIND: Record<LoginEventStatus, BadgeKind> = {
    success: 'approved',
    bad_password: 'rejected',
    unknown_email: 'rejected',
    // device_mismatch is a buddy-punching/credential-sharing signal — flag
    // it visually, distinct from a plain bad password.
    device_mismatch: 'alert',
    mfa_required: 'info',
    mfa_failed: 'rejected',
    mfa_setup: 'info',
    deactivated: 'pending'
  };
</script>

<div class="tym-page">
  <p class="tym-section-sub mb-4">
    Every login decision, newest first. Device mismatch rows are flagged — possible buddy-punching or credential
    sharing.
  </p>
  <Toolbar>
    {#snippet filters()}
      <select class="input" aria-label="User" bind:value={userFilter}>
        <option value="">All users</option>
        {#each usersQuery.data ?? [] as u (u.id)}
          <option value={String(u.id)}>{u.name || u.email}</option>
        {/each}
      </select>
      <select class="input" aria-label="Status" bind:value={statusFilter}>
        <option value="">All statuses</option>
        {#each Object.entries(STATUS_LABEL) as [value, label] (value)}
          <option {value}>{label}</option>
        {/each}
      </select>
    {/snippet}
  </Toolbar>

  {#if query.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if query.isError}
    <div class="tym-error-state">Could not load login activity.</div>
  {:else if query.data}
    <DataTable
      mobileCards
      mobileColumns={['user', 'when', 'status', 'device']}
      columns={[
        { key: 'when', label: 'When', cell: whenCell },
        { key: 'user', label: 'User', cell: userCell },
        { key: 'status', label: 'Status', cell: statusCell },
        { key: 'ip', label: 'IP', cell: ipCell },
        { key: 'device', label: 'Device', cell: deviceCell }
      ]}
      rows={query.data}
      rowKey={(e) => e.id}
      emptyTitle="No login activity yet"
      emptyBody="Sign-in attempts will show up here as they happen."
    />
  {/if}
</div>

{#snippet whenCell(e: LoginEvent)}
  <span class="muted" title={new Date(e.created_at).toLocaleString()}>{relativeTime(e.created_at)}</span>
{/snippet}
{#snippet userCell(e: LoginEvent)}
  <span class="font-medium text-navy">{e.user_name || e.email}</span>
{/snippet}
{#snippet statusCell(e: LoginEvent)}
  <StatusBadge kind={STATUS_KIND[e.status]} label={STATUS_LABEL[e.status]} />
{/snippet}
{#snippet ipCell(e: LoginEvent)}
  <span class="muted">{e.ip || '—'}</span>
{/snippet}
{#snippet deviceCell(e: LoginEvent)}
  <span class="muted">{e.user_agent || '—'}</span>
{/snippet}
